<template>
  <div>
    <div class="heading-5 font-weight-medium">
      <span v-if="safeInfo.safeAddress"> Connect Existing Gnosis Safe</span>
      <span v-else>Create New Gnosis Safe</span>
    </div>
    <div class="mt-8">
      <div class="h1-26-m">Confirm Owners</div>
      <p class="p-14-r darkgray--text mt-3">
        This Safe has {{ safeInfo.owners.length }} owners. Please review and
        confirm the Safe account to connect.
      </p>
    </div>
    <div class="mt-5" v-if="safeInfo.safeAddress">
      <div class="p-12-r gray--text">Safe Address</div>
      <div class="d-flex align-center mt-2">
        <div class="p-16-r eth-address" id="safeAddress">
          {{ safeInfo.safeAddress }}
        </div>
        <div class="ml-5 d-flex align-center">
          <v-tooltip bottom color="dark">
            <template v-slot:activator="{ on, attrs }">
              <span
                v-bind="attrs"
                v-on="on"
                :class="['rounded-5 disabled icon-wrap', clipboardEleClass]"
                data-clipboard-target="#safeAddress"
              >
                <v-icon x-small class="gray--text">far fa-clone</v-icon>
              </span>
            </template>
            <span>Copy</span>
          </v-tooltip>
          <v-tooltip bottom color="dark">
            <template v-slot:activator="{ on, attrs }">
              <a
                v-bind="attrs"
                v-on="on"
                :href="
                  `${projectSelected.config.explorerAddrLink}${safeInfo.safeAddress}`
                "
                class="ml-3 text-decoration-none rounded-5 disabled icon-wrap"
                target="_blank"
                rel="noopener noreferrer"
              >
                <v-icon x-small class="gray--text"
                  >fas fa-external-link-alt</v-icon
                >
              </a>
            </template>
            <span>{{ projectSelected.config.explorerAddrTooltip }}</span>
          </v-tooltip>
        </div>
      </div>
    </div>
    <v-divider class="mt-5"></v-divider>
    <div class="mt-5">
      <div class="p-12-r gray--text">Safe Owners</div>
      <div
        class="d-flex align-center mt-2"
        v-for="owner in safeInfo.owners"
        :key="owner.address"
      >
        <div
          class="p-16-r eth-address"
          :id="`ownerAddress_${owner.address}`"
        >
          {{ owner.address }}
        </div>
        <div class="ml-5 d-flex align-center">
          <v-tooltip bottom color="dark">
            <template v-slot:activator="{ on, attrs }">
              <span
                v-bind="attrs"
                v-on="on"
                :class="['rounded-5 disabled icon-wrap', clipboardEleClass]"
                :data-clipboard-target="`#ownerAddress_${owner.address}`"
              >
                <v-icon x-small class="gray--text">far fa-clone</v-icon>
              </span>
            </template>
            <span>Copy</span>
          </v-tooltip>
          <v-tooltip bottom color="dark">
            <template v-slot:activator="{ on, attrs }">
              <a
                v-bind="attrs"
                v-on="on"
                :href="
                  `${projectSelected.config.explorerAddrLink}${owner.address}`
                "
                class="ml-3 text-decoration-none rounded-5 disabled icon-wrap"
                target="_blank"
                rel="noopener noreferrer"
              >
                <v-icon x-small class="gray--text"
                  >fas fa-external-link-alt</v-icon
                >
              </a>
            </template>
            <span>{{ projectSelected.config.explorerAddrTooltip }}</span>
          </v-tooltip>
        </div>
      </div>
    </div>
    <div class="mt-5">
      <div class="p-12-r gray--text">
        Any transaction requires the authorization of:
      </div>
      <div class="mt-2">
        {{ safeInfo.threshold }} out of {{ safeInfo.owners.length }} owners
      </div>
    </div>
    <div class="mt-10 d-flex align-center justify-space-between">
      <div class="p-14-r darkgray--text clickable" @click="back">Back</div>
      <div class="d-flex justify-end align-center">
        <div class="p-16-m gray--text clickable" @click="cancel">Cancel</div>
        <c-btn @click="nextStep" class="ml-5" :loading="submitting">
          <span v-if="safeInfo.safeAddress">Connect Safe</span>
          <span v-else>Create Safe</span>
        </c-btn>
      </div>
    </div>
    <tx-ethereum
      v-if="showCreatingTx"
      title="Create Gnosis Safe"
      :getRawTx="getCreateSafeRawTx"
      :walletAccount="safeInfo.creator"
      quiet
      hideProject
      @cancel="
        showCreatingTx = false;
        submitting = false;
      "
      @onSuccess="onCreateSafeSuccess"
    >
      <div class="d-flex mt-3 align-center">
        <div class="flex-2 text-left p-14-m text-uppercase gray--text">
          Safe Owners:
        </div>
        <div class="flex-3 h3-18-r">
          {{ safeInfo.owners.length }}
        </div>
      </div>
      <div class="d-flex mt-3 align-center">
        <div class="flex-2 text-left p-14-m text-uppercase gray--text">
          Multisig Threshold:
        </div>
        <div class="flex-3 h3-18-r">
          {{ safeInfo.threshold }}
        </div>
      </div>
    </tx-ethereum>
  </div>
</template>

<script>
import Clipboard from "clipboard";
import { mapGetters } from "vuex";
import { sleep } from "@/common/lib/utils";

export default {
  props: ["safeInfo"],
  computed: {
    ...mapGetters({
      projects: "projectConfig/projects",
    }),
    projectSelected() {
      return this.projects.find(pr => pr.name === "ethereum");
    },
  },
  data: () => ({
    submitting: false,
    clipboardEleClass: "",
    showCreatingTx: false,
  }),
  methods: {
    back() {
      this.$emit("setStep", "3");
    },
    cancel() {
      this.$store.dispatch("closeAuthModal");
    },
    addWallet(safeAddress) {
      this.$emit("next", safeAddress, {
        owners: this.safeInfo.owners,
        // signer: {
        //   address: this.safeInfo.signer.address,
        //   type: this.safeInfo.signer.type,
        //   metadata: this.safeInfo.signer.metadata,
        // },
        threshold: this.safeInfo.threshold,
      });
    },
    async getCreateSafeRawTx() {
      return this.$store.dispatch(
        "userWallets/gnosis/getCreateSafeRawTx",
        this.safeInfo
      );
    },
    async fetchSafeAddress(txHash) {
      const { failed, safeAddress } = await this.$store.dispatch(
        "userWallets/gnosis/fetchSafeAddressByTxHash",
        txHash
      );
      if (failed || safeAddress) {
        return { failed, safeAddress };
      }
      await sleep(2000);
      return this.fetchSafeAddress(txHash);
    },
    async onCreateSafeSuccess(txHash, signedTx) {
      let failed, safeAddress, error;
      await new Promise(async resolve => {
        if (signedTx) {
          try {
            const result = await this.$store.dispatch(
              "userWallets/gnosis/broadcastTx",
              signedTx
            );
            failed = !result.status;
            safeAddress = "0x" + result?.logs?.[0]?.data.slice(-40);
          } catch (e) {
            error = e;
          }
          resolve();
        } else {
          const result = await this.fetchSafeAddress(txHash);
          failed = result.failed;
          safeAddress = result.safeAddress;
          resolve();
        }
      });
      if (failed) {
        this.$store.dispatch("addAlert", {
          type: "error",
          title: "Cannot create Gnosis Safe",
          message:
            error?.message ?? "Something went wrong. Please try again later.",
        });
      }
      this.$store.dispatch("addAlert", {
        type: "success",
        title: "Success",
        message: "Your Gnosis Safe has been created.",
      });
      this.showCreatingTx = false;
      this.addWallet(safeAddress);
    },
    async nextStep() {
      if (this.safeInfo.safeAddress) return this.connectSafe();
      return this.createSafe();
    },
    async createSafe() {
      this.submitting = true;
      this.showCreatingTx = true;
    },
    connectSafe() {
      this.submitting = true;
      this.addWallet(this.safeInfo.safeAddress);
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.clipboardEleClass = "address-copy-" + new Date().valueOf();
      const clipboard = new Clipboard("." + this.clipboardEleClass);
      clipboard.on("success", () => {
        this.$store.dispatch("addAlert", {
          title: "Success",
          type: "success",
          message: "Wallet address copied to clipboard.",
        });
      });
    });
  },
};
</script>

<style lang="scss" scoped>
.icon-wrap {
  height: 28px;
  width: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.eth-address {
  width: 375px;
}
</style>
